import {
  HARMONY_FIRST_SESSION_ID,
  WORLD_SESSION_TIMES_MAX,
  type WorldRoomEntry,
} from "@cleo/contracts";

/**
 * What one retirement answered the tidy: the Desk's worldSessionRetirement.ts
 * RetirementOutcome, or the browser host's answer from the World's own door
 * (worldSessionRetirementRail:retireOwnSession), each with the reason word and
 * the World's own words for a refusal.
 */
export type TidyRetirementOutcome =
  | { readonly ok: true }
  | { readonly ok: false; readonly reason: string; readonly detail?: string | undefined };

/**
 * Can 80, layer 2 (the principal, 17 Oct 2026: "archive all sessions ... more
 * than 3 days old (or 1 day, if I ask) ... either all rooms, or a specific
 * room", and "Your own only"): the ONE tidy operation behind the rail's Tidy
 * up and Cleo's archive_quiet_sessions.
 *
 * Laws:
 * - Own only: a session is a candidate through a conversation on this Desk
 *   (projection_threads), or (the principal, 17 Oct 2026: "tidying the pocket
 *   sessions too") as a session in the person's own Pocket room that the
 *   World says this person opened. Either way the tidy reaches only the
 *   person's own work; retirement itself refuses history kept on another
 *   device, so a Pocket session whose history the World does not hold whole
 *   is kept, never attempted.
 * - A Pocket session is quiet by the World's time for it (its latest
 *   execution or retirement step, else its birth), as the rail's Earlier is.
 * - So is (Jason, 18 Oct 2026: "in our designer room, I show 133 sessions, I
 *   tried to archive past 3 days, but said none were older than 1 day?") a
 *   session in any room the tidy covers that the World says this person
 *   opened and this Desk holds no conversation for: one made on another Mac,
 *   or opened in the rail and never written in. The rail's Earlier counts
 *   every open session of a room, so the tidy reads the same rooms.
 * - A Pocket room's standing session (S_pocket_<actor>, its "Cleo") is never a
 *   candidate: Pocket opens its room with it before every new conversation,
 *   and the World refuses that once the session is retired.
 * - Quiet: every conversation of the session untouched since the cutoff; none
 *   pinned, running or waiting on the person; none the caller asked to keep.
 * - A session whose hand (a session launched from one of its conversations)
 *   stays is kept too: the World refuses to retire a session with an open
 *   session under it. Hands go before the session that launched them. The
 *   World's own links count (W-9b736ece: a Designer session whose hands were
 *   made on another Mac was offered, then refused): a hand is any open session
 *   the World says sits under it, wherever it was made.
 * - The World confirms each candidate by its one entry (never the room
 *   index, W-b853d3b9): open, and in the named room when one is named.
 * - The World's standing first session (S_harmony_session) is never a
 *   candidate: the World keeps it, and refuses to retire it.
 * - Archive = the existing retirement (conversations preserved first),
 *   several sessions at once (W-105e4e0a: one at a time was about 5 s a
 *   session on the principal's Desk, 30 sessions three to four minutes),
 *   the deepest hands first and a session only once every hand under it has
 *   gone, each refusal reported in the person's words (`why`) and the rest
 *   carried on. The World takes one lifecycle move at a time, so the
 *   reservations and commits queue per World (worldMoveQueue.ts) while the
 *   reads and uploads around them overlap.
 * - Scope by kind, as the rail's cards fold rooms (17 Oct 2026 review): the
 *   General card is every general room (`general`), a Workroom card every
 *   room of its Workroom (`workroomKey`), judged from each candidate's own
 *   entry, so a room whose sessions all folded under Earlier is never missed.
 * - Retirement is final in the World (no move reverses session.retired), so
 *   a session the Desk has seen retired is never read again (RetiredSessions),
 *   and before any entry the tidy asks the World which of the rest are
 *   retired, 64 a read (its session times): a Desk that has just started
 *   knows none of the sessions it archived before. Those are not candidates;
 *   every session the tidy confirms is still confirmed by its one entry.
 */

export const SESSION_TIDY_DAY_MS = 86_400_000;
/** One tidy archives at most this many sessions; the rest wait for the next. */
export const SESSION_TIDY_MAX = 300;
export const SESSION_TIDY_MAX_DAYS = 365;

export interface TidyThread {
  readonly threadId: string;
  readonly sessionId: string;
  readonly parentThreadId: string | null;
  /** The latest user message or turn, ms since the epoch; null when it has none. */
  readonly lastActivityMs: number | null;
  readonly createdMs: number;
  readonly pinned: boolean;
  /** Running, streaming, or waiting on an approval or the person's input. */
  readonly busy: boolean;
}

export interface TidyRequest {
  readonly worldKey: string;
  readonly days: number;
  /** One room, or every room when absent. */
  readonly roomId?: string | null | undefined;
  /** Several rooms, by id. */
  readonly roomIds?: ReadonlyArray<string> | undefined;
  /** Every general room: the rail's General card, or "my General sessions" to Cleo. */
  readonly general?: boolean | undefined;
  /** Every room of one Workroom: the rail's card for it. */
  readonly workroomKey?: string | undefined;
  /** Sessions the caller keeps (the rail passes the person's rail pins). */
  readonly keep?: ReadonlyArray<string> | undefined;
  readonly now: number;
}

export interface TidyKept {
  readonly pinned: number;
  /** A Pocket session whose history the World does not hold whole. */
  readonly elsewhere: number;
  readonly busy: number;
  readonly recent: number;
  /** Quiet, but a hand under it is still in use. */
  readonly handInUse: number;
  /** In another room than the one named. */
  readonly otherRoom: number;
  /**
   * The World did not confirm it as an open session: its read failed, the
   * World no longer has it, or a retirement of it is under way.
   */
  readonly unavailable: number;
  /**
   * Already archived (retired in the World), as the World's session times,
   * this Desk's memory of retirements or the session's own entry say. Not a
   * session that stays: there is nothing left to tidy.
   */
  readonly alreadyArchived: number;
}

export interface TidyItem {
  readonly sessionId: string;
  readonly label: string;
  readonly roomId: string;
  readonly roomLabel: string;
  /** General rooms count together as General, as the rail shows them. */
  readonly roomKind: "general" | "workroom";
  /** From the person's Pocket room (counted as Pocket). */
  readonly pocket?: true | undefined;
  /**
   * How many sessions launched it, one under another: a hand's is 1, a
   * hand's hand's 2; absent, 0. The tidy archives the deepest first, so a
   * session goes only once every hand under it has gone.
   */
  readonly depth?: number | undefined;
  /**
   * The session it sits under, when that session is archived in the same
   * tidy (W-9b736ece): the plan says so before the person approves.
   */
  readonly parentId?: string | undefined;
}

/**
 * The rail's card a tidied session belongs to, as the person sees it: Pocket,
 * General for every general room, else its Workroom room by name.
 */
export const tidyCard = (item: TidyItem): { readonly key: string; readonly label: string } =>
  item.pocket
    ? { key: "pocket", label: "Pocket" }
    : item.roomKind === "general"
      ? { key: "general", label: "General" }
      : { key: item.roomId, label: item.roomLabel };

/** A Pocket room's standing session: S_pocket_<actor> (backend lib/pocketConversationCopy.js pocketCoordinate). */
export const isPocketStandingSession = (sessionId: string): boolean =>
  sessionId.startsWith("S_pocket_");

/** One session of the person's Pocket room, as the World reads it now. */
export interface TidyPocketSession {
  readonly sessionId: string;
  readonly label: string;
  readonly roomId: string;
  /** Open in the World: not retired, no retirement reserved. */
  readonly open: boolean;
  /** Retired in the World (archived): the person's own count as already archived. */
  readonly retired?: boolean | undefined;
  /** When it was last active in the World (ms); null when the World gave no time. */
  readonly activeAt: number | null;
  readonly openedByReader: boolean;
  /** Every conversation it had is kept in the World and none is unresolved. */
  readonly historyInWorld: boolean;
}

/** A room the tidy may read for sessions this Desk holds none of. */
export interface TidyWorldRoom {
  readonly roomId: string;
  readonly kind: "general" | "workroom";
  readonly workroomKey: string | null;
}

/**
 * One session of a room in the tidy's scope that this Desk holds no
 * conversation for, open or retired, as the World reads it now. A Pocket
 * room's sessions are TidyPocketSession, except the sessions under them
 * (W-9b736ece, Tester run 20261022T083531Z-seat-test-3afb3f55): the Pocket
 * room's read lists only the sessions directly in it, so the person's own
 * Pocket room's sub-sessions come from the directory here, with `parentId`.
 */
export interface TidyWorldSession {
  readonly sessionId: string;
  readonly label: string;
  readonly roomId: string;
  readonly roomLabel: string;
  readonly roomKind: "general" | "workroom";
  /** When it was last active in the World (ms); null when the World gave no time. */
  readonly activeAt: number | null;
  readonly openedByReader: boolean;
  /** Retired in the World by the time it was read. */
  readonly retired?: boolean | undefined;
  /**
   * The session it sits under, when it is a hand (W-9b736ece). The World
   * refuses to retire a session while one under it is open, so these links
   * are read for every session of the rooms in scope, this Desk's own too.
   */
  readonly parentId?: string | undefined;
}

/** A quiet session kept because a session under it stays, with those sessions and why each stays. */
export type TidyHeld = TidyItem & {
  readonly hands: ReadonlyArray<{
    readonly sessionId: string;
    readonly label: string;
    readonly why: string;
  }>;
};

/** Every room; General; one room (by id or its Workroom); several rooms by id. */
export type TidyScope = "every-room" | "general" | "one-room" | "rooms";

/** A session the tidy did not archive: the machine reason, and the person's words for it. */
export type TidySkipped = TidyItem & { readonly reason: string; readonly why: string };

export interface TidyPlan {
  readonly worldKey: string;
  readonly days: number;
  readonly roomId: string | null;
  /** Narrowed to one or more rooms. */
  readonly scoped: boolean;
  /** What the tidy covers, for its words. */
  readonly scope: TidyScope;
  /** Children first. */
  readonly archive: ReadonlyArray<TidyItem>;
  readonly byRoom: ReadonlyArray<{
    readonly roomId: string;
    readonly roomLabel: string;
    readonly count: number;
  }>;
  readonly kept: TidyKept;
  /** Quiet sessions past SESSION_TIDY_MAX, left for the next tidy. */
  readonly remaining: number;
  /**
   * W-9b736ece: quiet sessions that stay because a session under them stays,
   * each with those sessions and why, so the preview names both (counted in
   * kept.handInUse). Only links the World read names; absent: none.
   */
  readonly held?: ReadonlyArray<TidyHeld>;
  /** The Pocket room could not be read now: this tidy leaves Pocket as it is (not "nothing to tidy"). */
  readonly pocketUnread: boolean;
  /** The rooms' sessions this Desk holds none of could not be read now: this tidy leaves them. */
  readonly worldUnread?: boolean;
  /**
   * Of the sessions to archive, those with a reply the World holds as never
   * settled: the retirement settles it first, as unconfirmed when nothing
   * will ever prove how it ended (W-d958415c), and keeps the session when it
   * may still run, so these may stay (said before the approval, not after).
   */
  readonly unsettled: number;
  /**
   * Of the sessions to archive, those whose last reply ran on another Mac or
   * an old copy of this Desk's data: it is closed as lost (not kept here) once
   * 14 days old, and a newer one stays (W-d958415c).
   */
  readonly fromAnotherDevice?: number;
}

export interface TidyResult {
  readonly plan: TidyPlan;
  readonly archived: ReadonlyArray<TidyItem>;
  readonly skipped: ReadonlyArray<TidySkipped>;
}

export interface SessionTidyDependencies {
  readonly readThreads: (worldKey: string) => Promise<ReadonlyArray<TidyThread>>;
  readonly readEntry: (input: {
    readonly worldKey: string;
    readonly sessionId: string;
  }) => Promise<WorldRoomEntry>;
  /** The person's Pocket room, every session in it (absent: the Desk's own sessions only). */
  readonly readPocketSessions?: (worldKey: string) => Promise<ReadonlyArray<TidyPocketSession>>;
  /**
   * The sessions of the rooms in scope (never a Pocket room), open or
   * retired, that this Desk holds no conversation for, with the World's time for each and
   * whether this person opened it (absent: the Desk's own sessions only).
   */
  readonly readWorldSessions?: (input: {
    readonly worldKey: string;
    readonly inScope: (room: TidyWorldRoom) => boolean;
    readonly heldHere: ReadonlySet<string>;
  }) => Promise<ReadonlyArray<TidyWorldSession>>;
  readonly retire: (input: {
    readonly worldKey: string;
    readonly sessionId: string;
    readonly roomId: string;
    readonly operationId: string;
  }) => Promise<TidyRetirementOutcome>;
  /**
   * Retirements in flight at once (absent: TIDY_RETIREMENTS_AT_ONCE, the
   * Desk's, whose move queue sends the World one move at a time). The
   * browser host, whose retirement is one World action each, sends one at a
   * time: the World refuses moves prepared at a cut its head has left
   * (worldMoveQueue.ts).
   */
  readonly retirementsAtOnce?: number | undefined;
  /** Sessions this Desk has seen retired (absent: every candidate is read). */
  readonly retired?: RetiredSessions | undefined;
  /**
   * Which of these sessions (at most WORLD_SESSION_TIMES_MAX) the World says
   * are retired, by its session-times read (absent: every candidate's entry
   * is read).
   */
  readonly readRetired?:
    | ((input: {
        readonly worldKey: string;
        readonly sessionIds: ReadonlyArray<string>;
      }) => Promise<ReadonlyArray<string>>)
    | undefined;
  /** After a tidy that archived anything, so the rail reads the World again. */
  readonly onArchived?: ((worldKey: string) => void) | undefined;
}

/**
 * The sessions this Desk has seen retired, per World. Retirement is final in
 * the World, and this Desk's conversations of retired sessions only grow with
 * every tidy (261 on the seat-test Desk on 17 Oct 2026, each one World read
 * per preview), so a known-retired session is counted, never read again.
 */
export class RetiredSessions {
  private readonly byWorld = new Map<string, Set<string>>();
  has(worldKey: string, sessionId: string): boolean {
    return this.byWorld.get(worldKey)?.has(sessionId) ?? false;
  }
  add(worldKey: string, sessionId: string): void {
    const known = this.byWorld.get(worldKey);
    if (known === undefined) this.byWorld.set(worldKey, new Set([sessionId]));
    else known.add(sessionId);
  }
}

const OPEN_DESCENDANT_WHY = "a session under it is still open";
const UNCONFIRMED_WHY = "could not be confirmed open just now";

/**
 * A skip in the person's words, from the retirement's reason and the World's
 * own words for a refusal. Unknown reasons say the session could not be
 * archived now, never a code.
 */
export function describeTidySkip(reason: string, detail?: string): string {
  const said = `${reason} ${detail ?? ""}`;
  if (/open descendant/i.test(said)) return OPEN_DESCENDANT_WHY;
  if (/history-on-another-device/.test(said) && /less than 14 days/.test(said))
    return "its last reply, from another Mac, is less than 14 days old";
  if (/history-on-another-device/.test(said)) return "its history is kept on another device";
  // The execution checks (worldExecutionCapture.ts): a reply the Desk cannot
  // prove finished, dispatched, started or recoverable.
  if (/execution-[a-z-]*(unresolved|unproven|unavailable)/.test(said))
    return "a reply in it never settled";
  if (/local-history-changed/.test(said)) return "it changed while it was being archived";
  if (/already-retired/.test(said)) return "it was already archived";
  if (/session-not-found/.test(said)) return "the World no longer has it";
  if (/world-access-refused/.test(said)) return "this Desk could not reach the World";
  if (/user-cancelled/.test(said)) return "it was cancelled";
  return "it could not be archived just now";
}

export class SessionTidyRefusal extends Error {
  readonly reason: string;
  constructor(reason: string) {
    super(reason);
    this.reason = reason;
  }
}

export function validTidyDays(days: number): boolean {
  return Number.isInteger(days) && days >= 1 && days <= SESSION_TIDY_MAX_DAYS;
}

interface LocalSession {
  readonly sessionId: string;
  readonly threads: ReadonlyArray<TidyThread>;
  readonly lastActivityMs: number;
}

/** Stage one, this Desk only: which of the person's sessions are quiet, and the parent links between them. */
export function selectQuietLocalSessions(
  threads: ReadonlyArray<TidyThread>,
  request: TidyRequest,
): {
  readonly quiet: ReadonlyArray<LocalSession>;
  readonly parentSessionOf: ReadonlyMap<string, string>;
  readonly kept: Pick<TidyKept, "pinned" | "busy" | "recent" | "handInUse">;
  /** Which sessions each kept count names, so a narrowed tidy can count its own rooms'. */
  readonly keptIds: Readonly<
    Record<"pinned" | "busy" | "recent" | "handInUse", ReadonlyArray<string>>
  >;
} {
  const cutoff = request.now - request.days * SESSION_TIDY_DAY_MS;
  const keep = new Set(request.keep ?? []);
  const bySession = new Map<string, TidyThread[]>();
  for (const thread of threads) {
    if (thread.sessionId === HARMONY_FIRST_SESSION_ID || isPocketStandingSession(thread.sessionId))
      continue;
    const list = bySession.get(thread.sessionId);
    if (list === undefined) bySession.set(thread.sessionId, [thread]);
    else list.push(thread);
  }
  const sessionOfThread = new Map(threads.map((thread) => [thread.threadId, thread.sessionId]));
  // A hand's session hangs under the session whose conversation launched it.
  const parentSessionOf = new Map<string, string>();
  for (const thread of threads) {
    const parent =
      thread.parentThreadId === null ? undefined : sessionOfThread.get(thread.parentThreadId);
    if (parent !== undefined && parent !== thread.sessionId)
      parentSessionOf.set(thread.sessionId, parent);
  }
  const keptIds = {
    pinned: [] as string[],
    busy: [] as string[],
    recent: [] as string[],
    handInUse: [] as string[],
  };
  const quietIds = new Set<string>();
  const sessions = new Map<string, LocalSession>();
  for (const [sessionId, list] of bySession) {
    const lastActivityMs = Math.max(
      ...list.map((thread) => thread.lastActivityMs ?? thread.createdMs),
    );
    sessions.set(sessionId, { sessionId, threads: list, lastActivityMs });
    if (list.some((thread) => thread.pinned) || keep.has(sessionId)) keptIds.pinned.push(sessionId);
    else if (list.some((thread) => thread.busy)) keptIds.busy.push(sessionId);
    else if (lastActivityMs >= cutoff) keptIds.recent.push(sessionId);
    else quietIds.add(sessionId);
  }
  // A quiet session with a hand that stays, stays (and so on up the chain).
  let changed = true;
  while (changed) {
    changed = false;
    for (const [child, parent] of parentSessionOf) {
      if (quietIds.has(parent) && !quietIds.has(child) && bySession.has(child)) {
        quietIds.delete(parent);
        keptIds.handInUse.push(parent);
        changed = true;
      }
    }
  }
  const quiet = [...quietIds]
    .map((id) => sessions.get(id)!)
    .sort((a, b) => a.lastActivityMs - b.lastActivityMs);
  const kept = {
    pinned: keptIds.pinned.length,
    busy: keptIds.busy.length,
    recent: keptIds.recent.length,
    handInUse: keptIds.handInUse.length,
  };
  return { quiet, parentSessionOf, kept, keptIds };
}

/** Stage one for Pocket: the person's own quiet Pocket conversations this Desk holds none of, oldest first. */
export function selectQuietPocketSessions(
  sessions: ReadonlyArray<TidyPocketSession>,
  heldHere: ReadonlySet<string>,
  request: TidyRequest,
): {
  readonly quiet: ReadonlyArray<TidyItem>;
  readonly kept: Pick<
    TidyKept,
    "pinned" | "recent" | "elsewhere" | "unavailable" | "alreadyArchived"
  >;
} {
  const cutoff = request.now - request.days * SESSION_TIDY_DAY_MS;
  const keep = new Set(request.keep ?? []);
  const kept = { pinned: 0, recent: 0, elsewhere: 0, unavailable: 0, alreadyArchived: 0 };
  const quiet: Array<TidyItem & { readonly at: number }> = [];
  for (const session of sessions) {
    // This Desk's own conversations are judged by stage one; another member's are not the person's.
    if (heldHere.has(session.sessionId) || !session.openedByReader) continue;
    if (isPocketStandingSession(session.sessionId)) continue;
    // Can 80's Tester (18 Oct 2026): a second look after archiving 65 Pocket
    // conversations counted none of them as already archived.
    if (session.retired === true) {
      kept.alreadyArchived++;
      continue;
    }
    if (!session.open) continue;
    if (session.activeAt === null) kept.unavailable++;
    else if (keep.has(session.sessionId)) kept.pinned++;
    else if (session.activeAt >= cutoff) kept.recent++;
    else if (!session.historyInWorld) kept.elsewhere++;
    else
      quiet.push({
        sessionId: session.sessionId,
        label: session.label,
        roomId: session.roomId,
        roomLabel: "Pocket",
        roomKind: "general",
        pocket: true,
        at: session.activeAt,
      });
  }
  quiet.sort((a, b) => a.at - b.at);
  return { quiet: quiet.map(({ at: _at, ...item }) => item), kept };
}

/**
 * Stage one for the rooms' sessions this Desk holds none of: the person's own
 * quiet ones, oldest first, judged by the World's time as Pocket's are.
 */
export function selectQuietWorldSessions(
  sessions: ReadonlyArray<TidyWorldSession>,
  heldHere: ReadonlySet<string>,
  request: TidyRequest,
): {
  readonly quiet: ReadonlyArray<TidyItem>;
  readonly kept: Pick<TidyKept, "pinned" | "recent" | "unavailable" | "alreadyArchived">;
} {
  const cutoff = request.now - request.days * SESSION_TIDY_DAY_MS;
  const keep = new Set(request.keep ?? []);
  const kept = { pinned: 0, recent: 0, unavailable: 0, alreadyArchived: 0 };
  const quiet: Array<TidyItem & { readonly at: number }> = [];
  for (const session of sessions) {
    // Another member's sessions are never the person's to tidy.
    if (heldHere.has(session.sessionId) || !session.openedByReader) continue;
    // A Pocket room's own sessions are read by readPocketSessions; the
    // sessions under them only here (W-9b736ece).
    const pocket = isPocketRoom(session.roomId);
    if (
      session.sessionId === HARMONY_FIRST_SESSION_ID ||
      isPocketStandingSession(session.sessionId) ||
      (pocket && session.parentId === undefined)
    )
      continue;
    if (session.retired === true) kept.alreadyArchived++;
    else if (session.activeAt === null) kept.unavailable++;
    else if (keep.has(session.sessionId)) kept.pinned++;
    else if (session.activeAt >= cutoff) kept.recent++;
    else
      quiet.push({
        sessionId: session.sessionId,
        label: session.label,
        roomId: session.roomId,
        roomLabel: pocket ? "Pocket" : session.roomLabel,
        roomKind: pocket ? "general" : session.roomKind,
        ...(pocket ? { pocket: true as const } : {}),
        at: session.activeAt,
      });
  }
  quiet.sort((a, b) => a.at - b.at);
  return { quiet: quiet.map(({ at: _at, ...item }) => item), kept };
}

/** The person's Pocket room: R_pocket_<actor> (backend lib/pocketConversationCopy.js). */
export const isPocketRoom = (roomId: string): boolean => roomId.startsWith("R_pocket_");

const depthOf = (sessionId: string, parentSessionOf: ReadonlyMap<string, string>): number => {
  let depth = 0;
  const seen = new Set<string>();
  for (
    let at = parentSessionOf.get(sessionId);
    at !== undefined && !seen.has(at);
    at = parentSessionOf.get(at)
  ) {
    seen.add(at);
    depth++;
  }
  return depth;
};

export async function mapBounded<T, R>(
  items: ReadonlyArray<T>,
  limit: number,
  f: (item: T) => Promise<R>,
): Promise<R[]> {
  const out: R[] = [];
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const at = next++;
      out[at] = await f(items[at]!);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return out;
}

/** A cold read of 64 sessions' times took about 0.85 s on the seat-test Desk (17 Oct 2026). */
const RETIRED_READS_AT_ONCE = 8;

/**
 * Which of `sessionIds` the World says are retired, WORLD_SESSION_TIMES_MAX a
 * read and up to RETIRED_READS_AT_ONCE reads at once; each is remembered in
 * deps.retired. A read that fails says nothing, so those sessions' entries
 * are read instead.
 */
async function askWorldRetired(
  worldKey: string,
  sessionIds: ReadonlyArray<string>,
  deps: SessionTidyDependencies,
): Promise<ReadonlySet<string>> {
  const said = new Set<string>();
  const readRetired = deps.readRetired;
  if (readRetired === undefined) return said;
  const batches: Array<ReadonlyArray<string>> = [];
  for (let at = 0; at < sessionIds.length; at += WORLD_SESSION_TIMES_MAX)
    batches.push(sessionIds.slice(at, at + WORLD_SESSION_TIMES_MAX));
  await mapBounded(batches, RETIRED_READS_AT_ONCE, async (batch) => {
    let retired: ReadonlyArray<string>;
    try {
      retired = await readRetired({ worldKey, sessionIds: batch });
    } catch {
      return;
    }
    const asked = new Set(batch);
    for (const sessionId of retired)
      if (asked.has(sessionId)) {
        said.add(sessionId);
        deps.retired?.add(worldKey, sessionId);
      }
  });
  return said;
}

/** The preview: what one tidy would archive, and what it keeps and why. Reads only. */
export async function planSessionTidy(
  request: TidyRequest,
  deps: SessionTidyDependencies,
): Promise<TidyPlan> {
  if (!validTidyDays(request.days)) throw new SessionTidyRefusal("days-invalid");
  const roomId = request.roomId ?? null;
  const roomIds =
    roomId !== null || (request.roomIds?.length ?? 0) > 0
      ? new Set([...(roomId === null ? [] : [roomId]), ...(request.roomIds ?? [])])
      : null;
  const general = request.general === true;
  const workroomKey = request.workroomKey ?? null;
  const scope: TidyScope =
    roomIds === null && !general && workroomKey === null
      ? "every-room"
      : general && roomIds === null && workroomKey === null
        ? "general"
        : (roomIds?.size ?? 1) === 1 && !general
          ? "one-room"
          : "rooms";
  // A room is in scope when it matches every narrowing asked for.
  const inScope = (room: { roomId: string; kind: string; workroomKey: string | null }) =>
    (roomIds === null || roomIds.has(room.roomId)) &&
    (!general || room.kind !== "workroom") &&
    (workroomKey === null || (room.kind === "workroom" && room.workroomKey === workroomKey));
  const threads = await deps.readThreads(request.worldKey);
  // The person's Pocket conversations this Desk holds no conversation for,
  // read while this Desk's own are judged (null: Pocket could not be read).
  const readPocket = deps.readPocketSessions;
  const pocketRead: Promise<ReadonlyArray<TidyPocketSession> | null> =
    readPocket === undefined
      ? Promise.resolve([])
      : Promise.resolve(request.worldKey)
          .then(readPocket)
          .catch(() => null);
  const heldHere = new Set(threads.map((thread) => thread.sessionId));
  // The rooms' sessions this Desk holds none of, read alongside (null: they
  // could not be read).
  const readWorld = deps.readWorldSessions;
  const worldRead: Promise<ReadonlyArray<TidyWorldSession> | null> =
    readWorld === undefined
      ? Promise.resolve([])
      : Promise.resolve({ worldKey: request.worldKey, inScope, heldHere })
          .then(readWorld)
          .catch(() => null);
  const local = selectQuietLocalSessions(threads, request);
  // A narrowed tidy also reads each kept session's room (below).
  const keptIds = scope === "every-room" ? [] : [...new Set(Object.values(local.keptIds).flat())];
  const saidRetired = await askWorldRetired(
    request.worldKey,
    [...new Set([...local.quiet.map((session) => session.sessionId), ...keptIds])].filter(
      (id) => !deps.retired?.has(request.worldKey, id),
    ),
    deps,
  );
  const knownRetired = (sessionId: string) =>
    saidRetired.has(sessionId) || (deps.retired?.has(request.worldKey, sessionId) ?? false);
  let otherRoom = 0;
  let unavailable = 0;
  let alreadyArchived = 0;
  const toRead = local.quiet.filter((session) => {
    if (!knownRetired(session.sessionId)) return true;
    alreadyArchived++;
    return false;
  });
  const entries = await mapBounded(toRead, 6, async (session) => {
    try {
      return await deps.readEntry({ worldKey: request.worldKey, sessionId: session.sessionId });
    } catch {
      return null;
    }
  });
  const confirmed: TidyItem[] = [];
  // Why a session that is not archived stays, in the person's words, for a
  // session it keeps above it (W-9b736ece).
  const quietFor = request.days === 1 ? "day" : `${request.days} days`;
  const staysWhy = new Map<string, string>();
  for (const id of local.keptIds.pinned) staysWhy.set(id, "is pinned or open");
  for (const id of local.keptIds.busy) staysWhy.set(id, "is working or waiting on you");
  for (const id of local.keptIds.recent) staysWhy.set(id, `was used in the last ${quietFor}`);
  for (const id of local.keptIds.handInUse) staysWhy.set(id, "has a session under it that stays");
  const unsettledIds = new Set<string>();
  const anotherDeviceIds = new Set<string>();
  toRead.forEach((session, at) => {
    const entry = entries[at];
    if (entry?.session.sessionId === session.sessionId && entry.session.lifecycle === "retired") {
      deps.retired?.add(request.worldKey, session.sessionId);
      alreadyArchived++;
      return;
    }
    if (
      entry == null ||
      entry.session.sessionId !== session.sessionId ||
      entry.session.retirement != null
    ) {
      unavailable++;
      staysWhy.set(session.sessionId, UNCONFIRMED_WHY);
      return;
    }
    if (!inScope({ ...entry.room, workroomKey: entry.room.workroomKey ?? null })) {
      otherRoom++;
      return;
    }
    const unresolved = entry.session.unresolvedExecutions ?? [];
    // W-d958415c: a reply that ran in a conversation this Desk does not hold
    // (another Mac, or an old copy of this Desk's data) is closed as lost once
    // the World judges it 14 days old (the principal, 18 Oct 2026: "B",
    // "14 days"); a newer one keeps the session, as the preview says.
    if (
      unresolved.some(
        (execution) => !session.threads.some((thread) => thread.threadId === execution.threadId),
      )
    )
      anotherDeviceIds.add(session.sessionId);
    else if (unresolved.length > 0) unsettledIds.add(session.sessionId);
    confirmed.push({
      sessionId: session.sessionId,
      label: entry.session.label,
      roomId: entry.room.roomId,
      roomLabel: entry.room.label,
      roomKind: entry.room.kind === "workroom" ? "workroom" : "general",
    });
  });
  const pocketAnswer = await pocketRead;
  const pocketUnread = pocketAnswer === null;
  const pocketSessions = pocketAnswer ?? [];
  const pocket = selectQuietPocketSessions(pocketSessions, heldHere, request);
  for (const item of pocket.quiet)
    if (!inScope({ roomId: item.roomId, kind: "general", workroomKey: null })) otherRoom++;
    else confirmed.push(item);
  // The person's own quiet sessions this Desk holds none of: each confirmed
  // by its one entry, as this Desk's own are; its hands go before it.
  const worldAnswer = await worldRead;
  const worldUnread = worldAnswer === null;
  const world = selectQuietWorldSessions(worldAnswer ?? [], heldHere, request);
  const parentSessionOf = new Map(local.parentSessionOf);
  let worldElsewhere = 0;
  const worldEntries = await mapBounded(world.quiet, 6, async (item) => {
    try {
      return await deps.readEntry({ worldKey: request.worldKey, sessionId: item.sessionId });
    } catch {
      return null;
    }
  });
  world.quiet.forEach((item, at) => {
    const entry = worldEntries[at];
    if (entry?.session.sessionId === item.sessionId && entry.session.lifecycle === "retired") {
      deps.retired?.add(request.worldKey, item.sessionId);
      alreadyArchived++;
      return;
    }
    if (
      entry == null ||
      entry.session.sessionId !== item.sessionId ||
      entry.session.retirement != null
    ) {
      unavailable++;
      staysWhy.set(item.sessionId, UNCONFIRMED_WHY);
      return;
    }
    if (!inScope({ ...entry.room, workroomKey: entry.room.workroomKey ?? null })) {
      otherRoom++;
      return;
    }
    // Every conversation it had must be kept in the World: one held on
    // another device only, with no reply left to close as lost, would be
    // refused by the retirement, so it stays.
    const unresolved = entry.session.unresolvedExecutions ?? [];
    const accounted = new Set([
      ...(entry.session.transcripts ?? []).map((row) => row.threadId),
      ...(entry.session.lostThreads ?? []),
      ...unresolved.map((row) => row.threadId),
    ]);
    if ((entry.session.participants ?? []).some((row) => !accounted.has(row.threadId))) {
      worldElsewhere++;
      staysWhy.set(item.sessionId, "has its history kept on another device");
      return;
    }
    if (unresolved.length > 0) anotherDeviceIds.add(item.sessionId);
    if (entry.session.parentId.startsWith("S_"))
      parentSessionOf.set(item.sessionId, entry.session.parentId);
    confirmed.push({
      ...item,
      label: entry.session.label,
      roomId: entry.room.roomId,
      ...(item.pocket
        ? {}
        : {
            roomLabel: entry.room.label,
            roomKind: entry.room.kind === "workroom" ? ("workroom" as const) : ("general" as const),
          }),
    });
  });
  // A narrowed tidy says what stays in its own rooms (17 Oct 2026: a CRM tidy
  // said "1 pinned or open, 3 working" of sessions in other rooms): each kept
  // session's room is read from its one entry, as a candidate's is.
  let keptLocal = local.kept;
  if (scope !== "every-room") {
    const ids = keptIds.filter((id) => !knownRetired(id));
    const read = await mapBounded(ids, 6, async (sessionId) => {
      try {
        return await deps.readEntry({ worldKey: request.worldKey, sessionId });
      } catch {
        return null;
      }
    });
    const here = new Set(
      ids.filter((id, at) => {
        const entry = read[at];
        return (
          entry != null &&
          entry.session.sessionId === id &&
          entry.session.lifecycle !== "retired" &&
          inScope({ ...entry.room, workroomKey: entry.room.workroomKey ?? null })
        );
      }),
    );
    const count = (list: ReadonlyArray<string>) => list.filter((id) => here.has(id)).length;
    keptLocal = {
      pinned: count(local.keptIds.pinned),
      busy: count(local.keptIds.busy),
      recent: count(local.keptIds.recent),
      handInUse: count(local.keptIds.handInUse),
    };
  }
  const pocketHere =
    pocketSessions.length === 0 ||
    inScope({ roomId: pocketSessions[0]!.roomId, kind: "general", workroomKey: null });
  const pocketKept = pocketHere
    ? pocket.kept
    : { ...pocket.kept, pinned: 0, recent: 0, elsewhere: 0, alreadyArchived: 0 };
  // W-9b736ece: the World's links between the rooms' sessions, this Desk's
  // own included. A hand counts for its order, and a session with an open
  // hand that is not going stays (and so on up the chain).
  const openHandsOf = new Map<string, string[]>();
  for (const session of worldAnswer ?? []) {
    if (session.parentId === undefined || session.retired === true) continue;
    if (deps.retired?.has(request.worldKey, session.sessionId)) continue;
    if (!parentSessionOf.has(session.sessionId))
      parentSessionOf.set(session.sessionId, session.parentId);
    const hands = openHandsOf.get(session.parentId);
    if (hands === undefined) openHandsOf.set(session.parentId, [session.sessionId]);
    else hands.push(session.sessionId);
  }
  const going = new Set(confirmed.slice(0, SESSION_TIDY_MAX).map((item) => item.sessionId));
  for (const item of confirmed.slice(SESSION_TIDY_MAX))
    staysWhy.set(item.sessionId, "waits for the next tidy");
  const cutoff = request.now - request.days * SESSION_TIDY_DAY_MS;
  const keep = new Set(request.keep ?? []);
  const worldRow = new Map((worldAnswer ?? []).map((row) => [row.sessionId, row]));
  const whyHandStays = (hand: string): string => {
    const said = staysWhy.get(hand);
    if (said !== undefined) return said;
    const row = worldRow.get(hand);
    if (row !== undefined && !row.openedByReader) return "is another person's";
    if (keep.has(hand)) return "is pinned or open";
    if (row?.activeAt != null && row.activeAt >= cutoff) return `was used in the last ${quietFor}`;
    return UNCONFIRMED_WHY;
  };
  const labelOf = new Map<string, string>([
    ...(worldAnswer ?? []).map((row) => [row.sessionId, row.label] as const),
    ...pocketSessions.map((row) => [row.sessionId, row.label] as const),
    ...confirmed.map((item) => [item.sessionId, item.label] as const),
  ]);
  const confirmedById = new Map(confirmed.map((item) => [item.sessionId, item]));
  const held: TidyHeld[] = [];
  let handKept = 0;
  for (let changed = true; changed;) {
    changed = false;
    for (const sessionId of going) {
      const staying = (openHandsOf.get(sessionId) ?? []).filter((hand) => !going.has(hand));
      if (staying.length > 0) {
        going.delete(sessionId);
        handKept++;
        changed = true;
        held.push({
          ...confirmedById.get(sessionId)!,
          hands: staying.map((hand) => ({
            sessionId: hand,
            label: labelOf.get(hand) ?? "a session",
            why: whyHandStays(hand),
          })),
        });
        staysWhy.set(sessionId, "has a session under it that stays");
      }
    }
  }
  // Tester run 20261022T093751Z-seat-test-ce5db682 (#2866): "New room" (S_8cd650ee…) stayed because its sub-session
  // "Lighthouse helper 1019-0835" (S_964539aa…) waits on the person, both held on this Desk, so stage one kept it
  // before any World read and the preview only counted "2 with a hand still in use". Each such session is named too,
  // with the sessions under it that stay, when the World's read of the rooms in scope names it.
  const localHandsOf = new Map<string, string[]>();
  for (const [child, parent] of local.parentSessionOf) {
    const hands = localHandsOf.get(parent);
    if (hands === undefined) localHandsOf.set(parent, [child]);
    else hands.push(child);
  }
  for (const sessionId of local.keptIds.handInUse) {
    const row = worldRow.get(sessionId);
    if (row === undefined || row.retired === true || knownRetired(sessionId)) continue;
    const staying = [
      ...new Set([...(localHandsOf.get(sessionId) ?? []), ...(openHandsOf.get(sessionId) ?? [])]),
    ].filter(
      (hand) => !going.has(hand) && !knownRetired(hand) && worldRow.get(hand)?.retired !== true,
    );
    if (staying.length === 0) continue;
    const pocket = isPocketRoom(row.roomId);
    held.push({
      sessionId,
      label: row.label,
      roomId: row.roomId,
      roomLabel: pocket ? "Pocket" : row.roomLabel,
      roomKind: pocket ? "general" : row.roomKind,
      ...(pocket ? { pocket: true as const } : {}),
      hands: staying.map((hand) => ({
        sessionId: hand,
        label: labelOf.get(hand) ?? "a session",
        why: whyHandStays(hand),
      })),
    });
  }
  const taken = confirmed.slice(0, SESSION_TIDY_MAX);
  const archive = taken
    .filter((item) => going.has(item.sessionId))
    .map((item, order) => ({ item, order, depth: depthOf(item.sessionId, parentSessionOf) }))
    .sort((a, b) => b.depth - a.depth || a.order - b.order)
    .map(({ item, depth }) => {
      const parentId = parentSessionOf.get(item.sessionId);
      return {
        ...item,
        ...(depth === 0 ? {} : { depth }),
        ...(parentId !== undefined && going.has(parentId) ? { parentId } : {}),
      };
    });
  // The rail's grouping: every general room under General, each Workroom by itself.
  const rooms = new Map<string, { roomId: string; roomLabel: string; count: number }>();
  for (const item of archive) {
    const card = tidyCard(item);
    const room = rooms.get(card.key);
    if (room) room.count++;
    else rooms.set(card.key, { roomId: card.key, roomLabel: card.label, count: 1 });
  }
  return {
    worldKey: request.worldKey,
    days: request.days,
    roomId,
    scoped: scope !== "every-room",
    scope,
    archive,
    byRoom: [...rooms.values()].sort((a, b) => b.count - a.count),
    kept: {
      ...keptLocal,
      handInUse: keptLocal.handInUse + handKept,
      pinned: keptLocal.pinned + pocketKept.pinned + world.kept.pinned,
      recent: keptLocal.recent + pocketKept.recent + world.kept.recent,
      elsewhere: pocketKept.elsewhere + worldElsewhere,
      otherRoom,
      unavailable: unavailable + pocketKept.unavailable + world.kept.unavailable,
      alreadyArchived: alreadyArchived + pocketKept.alreadyArchived + world.kept.alreadyArchived,
    },
    remaining: confirmed.length - taken.length,
    ...(held.length > 0 ? { held } : {}),
    pocketUnread,
    ...(worldUnread ? { worldUnread } : {}),
    unsettled: archive.filter((item) => unsettledIds.has(item.sessionId)).length,
    fromAnotherDevice: archive.filter((item) => anotherDeviceIds.has(item.sessionId)).length,
  };
}

/** The tidy itself: the preview recomputed now, then each session retired, hands first. */
export interface TidyProgress {
  readonly total: number;
  /** In the plan's order, those settled so far. */
  readonly archived: ReadonlyArray<TidyItem>;
  readonly skipped: ReadonlyArray<TidySkipped>;
}

/**
 * Retirements in flight at once (W-105e4e0a). Each is four or five World
 * round trips, about 0.6 s each on the principal's Desk, and a transcript
 * upload per conversation; the Desk's own part (two local inspections, the
 * secrets file read and the grant selected per call) is under 3 ms a session.
 * Of those, the two moves (the reservation and the commit) go one at a time
 * per World, as the World's history requires (worldMoveQueue.ts); the entry
 * read, the uploads and the read-back of these six overlap.
 */
export const TIDY_RETIREMENTS_AT_ONCE = 6;

export async function runSessionTidy(
  request: TidyRequest & {
    readonly operationId: string;
    /** The count the person approved; a different count now archives nothing. */
    readonly expectCount?: number | undefined;
  },
  deps: SessionTidyDependencies,
  onProgress?: (progress: TidyProgress) => void,
): Promise<TidyResult> {
  if (request.operationId.trim().length === 0) throw new SessionTidyRefusal("operation-required");
  const plan = await planSessionTidy(request, deps);
  if (request.expectCount !== undefined && plan.archive.length !== request.expectCount)
    throw new SessionTidyRefusal(
      `count-changed: the tidy now covers ${plan.archive.length} session${plan.archive.length === 1 ? "" : "s"}, not the ${request.expectCount} approved; preview it again`,
    );
  // Each session's outcome at its place in the plan, so progress and the
  // result keep the plan's order whatever order the retirements settle in.
  const settled = new Map<
    number,
    { readonly archived: TidyItem } | { readonly skipped: TidySkipped }
  >();
  const archivedSoFar = () =>
    plan.archive.flatMap((_item, at) => {
      const outcome = settled.get(at);
      return outcome !== undefined && "archived" in outcome ? [outcome.archived] : [];
    });
  // W-9b736ece: the sessions the plan kept because a session under them stays are among those that stayed, named
  // with that session and why (Tester run 20261022T093751Z-seat-test-ce5db682: the result named neither).
  const heldSkipped: ReadonlyArray<TidySkipped> = (plan.held ?? []).map(({ hands, ...item }) => ({
    ...item,
    reason: `kept-by-session-under-it: ${hands.map((hand) => hand.sessionId).join(", ")}`,
    why: `it stayed because ${handsWhyWords(hands)}`,
  }));
  const skippedSoFar = () => [
    ...plan.archive.flatMap((_item, at) => {
      const outcome = settled.get(at);
      return outcome !== undefined && "skipped" in outcome ? [outcome.skipped] : [];
    }),
    ...heldSkipped,
  ];
  const report = () =>
    onProgress?.({
      total: plan.archive.length,
      archived: archivedSoFar(),
      skipped: skippedSoFar(),
    });
  report();
  const retireOne = async (item: TidyItem, at: number) => {
    // W-9b736ece: a session whose own session under it stayed stays too; the
    // World is not asked (it would refuse), and the person reads which held it.
    const stayedUnder = plan.archive.find((hand, handAt) => {
      if (hand.parentId !== item.sessionId) return false;
      const outcome = settled.get(handAt);
      return outcome !== undefined && "skipped" in outcome;
    });
    if (stayedUnder !== undefined) {
      settled.set(at, {
        skipped: {
          ...item,
          reason: `open-descendant: ${stayedUnder.sessionId} stayed`,
          why: OPEN_DESCENDANT_WHY,
        },
      });
      report();
      return;
    }
    let outcome: TidyRetirementOutcome;
    try {
      outcome = await deps.retire({
        worldKey: request.worldKey,
        sessionId: item.sessionId,
        roomId: item.roomId,
        // One stable identity per session, so a repeated tidy resumes it.
        operationId: `${request.operationId}:${item.sessionId}`,
      });
    } catch (error) {
      const reason = error instanceof Error ? error.message : "retirement-unresolved";
      settled.set(at, { skipped: { ...item, reason, why: describeTidySkip(reason) } });
      report();
      return;
    }
    if (outcome.ok || outcome.reason === "already-retired")
      deps.retired?.add(request.worldKey, item.sessionId);
    settled.set(
      at,
      outcome.ok
        ? { archived: item }
        : {
            skipped: {
              ...item,
              reason: outcome.detail ? `${outcome.reason}: ${outcome.detail}` : outcome.reason,
              why: describeTidySkip(outcome.reason, outcome.detail),
            },
          },
    );
    report();
  };
  // Hands before the session that launched them: the deepest depth first,
  // every session of a depth together, the next depth once all have settled.
  const byDepth = new Map<number, Array<{ readonly item: TidyItem; readonly at: number }>>();
  plan.archive.forEach((item, at) => {
    const depth = item.depth ?? 0;
    const list = byDepth.get(depth);
    if (list === undefined) byDepth.set(depth, [{ item, at }]);
    else list.push({ item, at });
  });
  for (const depth of [...byDepth.keys()].sort((a, b) => b - a))
    await mapBounded(
      byDepth.get(depth)!,
      Math.max(1, Math.floor(deps.retirementsAtOnce ?? TIDY_RETIREMENTS_AT_ONCE)),
      ({ item, at }) => retireOne(item, at),
    );
  const archived = archivedSoFar();
  if (archived.length > 0) deps.onArchived?.(request.worldKey);
  return { plan, archived, skipped: skippedSoFar() };
}

/**
 * W-9b736ece (Tester, 22 Oct 2026): the World retires a session only once
 * every session under it has gone, so the tidy archives those first; the
 * person reads that before approving, for each session going with sessions
 * under it (the topmost of a chain), naming a few.
 */
const HANDS_LINES_MAX = 3;
const HANDS_NAMED_MAX = 3;

/**
 * "Designer has 1 session under it, “Notes”; it is archived first, with its
 * conversation kept." Names up to three; the rail says "retired", as its
 * menu does.
 */
export function sessionsUnderWords(
  label: string,
  handLabels: ReadonlyArray<string>,
  verb: "archived" | "retired" = "archived",
): string {
  const n = handLabels.length;
  const names = handLabels.map((hand) => `“${hand}”`);
  const named =
    n > HANDS_NAMED_MAX
      ? ""
      : n === 1
        ? `, ${names[0]}`
        : `, ${names.slice(0, -1).join(", ")} and ${names[n - 1]}`;
  return n === 1
    ? `${label} has 1 session under it${named}; it is ${verb} first, with its conversation kept.`
    : `${label} has ${n} sessions under it${named}; they are ${verb} first, with their conversations kept.`;
}
function handsLines(archive: ReadonlyArray<TidyItem>): ReadonlyArray<string> {
  const going = new Map(archive.map((item) => [item.sessionId, item]));
  const handsOf = new Map<string, TidyItem[]>();
  for (const item of archive) {
    if (item.parentId === undefined || !going.has(item.parentId)) continue;
    const hands = handsOf.get(item.parentId);
    if (hands === undefined) handsOf.set(item.parentId, [item]);
    else hands.push(item);
  }
  const under = (sessionId: string): TidyItem[] => {
    const all: TidyItem[] = [];
    const seen = new Set([sessionId]);
    const visit = (id: string) => {
      for (const hand of handsOf.get(id) ?? []) {
        if (seen.has(hand.sessionId)) continue;
        seen.add(hand.sessionId);
        all.push(hand);
        visit(hand.sessionId);
      }
    };
    visit(sessionId);
    return all;
  };
  const tops = archive.filter(
    (item) =>
      handsOf.has(item.sessionId) && (item.parentId === undefined || !going.has(item.parentId)),
  );
  const lines = tops.slice(0, HANDS_LINES_MAX).map((top) =>
    sessionsUnderWords(
      top.label,
      under(top.sessionId).map((hand) => hand.label),
    ),
  );
  const more = tops.length - HANDS_LINES_MAX;
  if (more > 0)
    lines.push(
      more === 1
        ? "1 more has sessions under it; those are archived first, with their conversations kept."
        : `${more} more have sessions under them; those are archived first, with their conversations kept.`,
    );
  return lines;
}

/** At most this many sessions that stayed are named one by one; the rest are counted. */
const STAYED_NAMED_MAX = 8;

/** "“A”", "“A” and “B”", "“A”, “B” and “C”". */
const namesWords = (labels: ReadonlyArray<string>): string => {
  const names = labels.map((label) => `“${label}”`);
  return names.length <= 1
    ? (names[0] ?? "")
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
};

/**
 * W-9b736ece (Tester run 20261022T070803Z-seat-test-97714648: Tidy up General
 * said "2 stayed: a session under it is still open; its last reply, from
 * another Mac, is less than 14 days old", naming neither): each session that
 * stayed, by the label its rail row shows and the rail card it sits in, with
 * why in the tidy's words. A session kept because a session under it stayed
 * names that one and why it stayed (Tester run 20261022T083531Z-seat-test-3afb3f55).
 */
export function describeTidyStayed(
  skipped: ReadonlyArray<TidySkipped>,
): ReadonlyArray<{ readonly key: string; readonly text: string }> {
  const stayedUnder = new Map<string, TidySkipped[]>();
  for (const item of skipped) {
    if (item.parentId === undefined) continue;
    const hands = stayedUnder.get(item.parentId);
    if (hands === undefined) stayedUnder.set(item.parentId, [item]);
    else hands.push(item);
  }
  const handWhy = (hand: TidySkipped): string => {
    const under = stayedUnder.get(hand.sessionId) ?? [];
    return under.length > 0 && hand.why === OPEN_DESCENDANT_WHY
      ? `${namesWords(under.map((row) => row.label))} under it stayed`
      : hand.why;
  };
  const lines = skipped.slice(0, STAYED_NAMED_MAX).map((item) => {
    const hands = stayedUnder.get(item.sessionId) ?? [];
    const why =
      hands.length === 0 || item.why !== OPEN_DESCENDANT_WHY
        ? item.why
        : hands.length === 1
          ? `it stayed because “${hands[0]!.label}” under it stayed (${handWhy(hands[0]!)})`
          : `it stayed because ${namesWords(hands.map((hand) => hand.label))} under it stayed`;
    return { key: item.sessionId, text: `“${item.label}” in ${tidyCard(item).label}: ${why}.` };
  });
  const more = skipped.length - STAYED_NAMED_MAX;
  if (more > 0) lines.push({ key: "more", text: `and ${more} more stayed.` });
  return lines;
}

/**
 * "“Draft” under it was used in the last day", naming up to three sessions under
 * a session, each with why it stays.
 */
function handsWhyWords(hands: TidyHeld["hands"]): string {
  const named = hands.slice(0, HANDS_NAMED_MAX);
  const said = named.map((hand) => `“${hand.label}” under it ${hand.why}`);
  const more = hands.length - named.length;
  return `${said.length <= 1 ? (said[0] ?? "") : `${said.slice(0, -1).join(", ")} and ${said[said.length - 1]}`}${more > 0 ? `, and ${more} more under it stay` : ""}`;
}

/** At most this many sessions kept by a session under them are named in the preview; the rest are counted. */
const HELD_NAMED_MAX = 3;

/**
 * W-9b736ece: before the approval, each quiet session that stays because a
 * session under it stays, naming both and why: "“Notes” in General stays:
 * “Draft” under it was used in the last day."
 */
function heldLines(held: ReadonlyArray<TidyHeld>): ReadonlyArray<string> {
  const lines = held
    .slice(0, HELD_NAMED_MAX)
    .map(
      (item) => `“${item.label}” in ${tidyCard(item).label} stays: ${handsWhyWords(item.hands)}.`,
    );
  const more = held.length - HELD_NAMED_MAX;
  if (more > 0)
    lines.push(
      more === 1
        ? "1 more stays because a session under it stays."
        : `${more} more stay because a session under them stays.`,
    );
  return lines;
}

/** The plan in the person's words: one line each, for a card or a dialog. */
export function describeTidyPlan(plan: TidyPlan): {
  readonly title: string;
  readonly lines: ReadonlyArray<string>;
} {
  const n = plan.archive.length;
  const quietFor = plan.days === 1 ? "a day" : `${plan.days} days`;
  const where =
    plan.scope === "every-room"
      ? "across your rooms"
      : plan.scope === "general"
        ? "in General"
        : plan.scope === "rooms"
          ? "in these rooms"
          : `in ${plan.archive[0]?.roomLabel ?? "this room"}`;
  // "Your": the tidy reaches only the person's own; a room's quiet sessions
  // under Earlier may be other people's (17 Oct 2026, CRM).
  const title =
    n === 0
      ? `None of your sessions${plan.scope === "every-room" ? "" : ` ${where}`} have been quiet for ${quietFor}`
      : `Archive ${n} of your sessions quiet for ${quietFor} ${where}`;
  const lines: string[] = plan.byRoom.slice(0, 8).map((room) => `${room.roomLabel}: ${room.count}`);
  if (plan.byRoom.length > 8) lines.push(`and ${plan.byRoom.length - 8} more rooms`);
  lines.push(...handsLines(plan.archive));
  const k = plan.kept;
  const stays = [
    k.pinned > 0 ? `${k.pinned} pinned or open` : null,
    k.busy > 0 ? `${k.busy} working or waiting on you` : null,
    k.handInUse > 0 ? `${k.handInUse} with a hand still in use` : null,
    k.elsewhere > 0 ? `${k.elsewhere} with history kept on another device` : null,
  ].filter((part): part is string => part !== null);
  if (stays.length > 0) lines.push(`Stays: ${stays.join(", ")}.`);
  lines.push(...heldLines(plan.held ?? []));
  // Can 80's Tester (18 Oct 2026): sessions archived before are their own
  // number, never among what stays or what goes.
  if (k.alreadyArchived > 0)
    lines.push(`Already archived before: ${k.alreadyArchived} (not counted in this tidy).`);
  if (plan.unsettled > 0)
    lines.push(
      plan.unsettled === 1
        ? "1 of them has a reply that never settled: it is closed as unconfirmed, with everything written kept, and stays only if it may still be running."
        : `${plan.unsettled} of them have a reply that never settled: each is closed as unconfirmed, with everything written kept, and stays only if it may still be running.`,
    );
  const away = plan.fromAnotherDevice ?? 0;
  if (away > 0)
    lines.push(
      away === 1
        ? "1 of them has a last reply from another Mac that this Desk never received: once that reply is 14 days old it is not kept, and the session is archived with everything else; a newer one stays."
        : `${away} of them have a last reply from another Mac that this Desk never received: once that reply is 14 days old it is not kept, and the session is archived with everything else; a newer one stays.`,
    );
  if (plan.remaining > 0) lines.push(`${plan.remaining} more wait for the next tidy.`);
  if (plan.pocketUnread)
    lines.push("Your Pocket conversations could not be read just now, so this tidy leaves them.");
  if (plan.worldUnread === true)
    lines.push(
      "Your sessions this Desk has no conversation for could not be read just now, so this tidy leaves them.",
    );
  if (n > 0)
    lines.push(
      "Archived sessions move to Retired. Their conversations are kept and readable; continuing one starts a fresh conversation that carries them.",
    );
  return { title, lines };
}

/**
 * A tidy outlives the call that started it: an engine cuts a tool call after
 * about a minute (dispatch_provider_targets caps its own wait at 45 s), and a
 * tidy of hundreds of sessions takes minutes. The first call with an
 * operation starts it; every call with the same operation, the first
 * included, waits at most `waitMs` and answers where it stands: as soon as
 * the tidy is done or has moved on since the call began (at most one answer
 * a second, so a tidy of hundreds is not hundreds of calls), else at `waitMs`.
 * Nothing starts twice. A tidy refused before it archived anything is
 * forgotten, so a fresh preview and approval can try again.
 */
export interface TidyRunView {
  readonly done: boolean;
  /** Null until the tidy has confirmed its sessions. */
  readonly total: number | null;
  readonly archived: ReadonlyArray<TidyItem>;
  readonly skipped: ReadonlyArray<TidySkipped>;
  readonly remaining: number;
}

interface TidyRun {
  progress: TidyProgress | null;
  result: TidyResult | null;
  error: string | null;
  settled: Promise<void>;
  readonly startedAt: number;
  /** Told once each time progress moves, then forgotten. */
  readonly watchers: Array<() => void>;
}

/** A join answers a moved tidy no sooner than this after it began. */
export const TIDY_PROGRESS_FLOOR_MS = 1_000;

export class SessionTidyRuns {
  private readonly runs = new Map<string, TidyRun>();

  async join(
    request: TidyRequest & {
      readonly operationId: string;
      readonly expectCount?: number | undefined;
    },
    deps: SessionTidyDependencies,
    waitMs: number,
    /** The wait's clock, supplied by the caller (an Effect sleep on the Desk). */
    sleep: (ms: number) => Promise<void>,
  ): Promise<TidyRunView> {
    const key = JSON.stringify([request.worldKey, request.operationId]);
    this.forgetOld(request.now);
    let run = this.runs.get(key);
    if (run === undefined) {
      const started: TidyRun = {
        progress: null,
        result: null,
        error: null,
        startedAt: request.now,
        settled: Promise.resolve(),
        watchers: [],
      };
      started.settled = runSessionTidy(request, deps, (progress) => {
        started.progress = progress;
        for (const watcher of started.watchers.splice(0)) watcher();
      }).then(
        (result) => {
          started.result = result;
        },
        (error: unknown) => {
          started.error = error instanceof Error ? error.message : "tidy-unresolved";
        },
      );
      this.runs.set(key, started);
      run = started;
    }
    const moved = new Promise<void>((resolve) => run.watchers.push(resolve));
    await Promise.race([
      run.settled,
      Promise.all([moved, sleep(Math.min(TIDY_PROGRESS_FLOOR_MS, waitMs))]),
      sleep(waitMs),
    ]);
    if (run.error !== null) {
      if ((run.progress?.archived.length ?? 0) === 0) this.runs.delete(key);
      throw new SessionTidyRefusal(run.error);
    }
    if (run.result !== null)
      return {
        done: true,
        total: run.result.plan.archive.length,
        archived: run.result.archived,
        skipped: run.result.skipped,
        remaining: run.result.plan.remaining,
      };
    return {
      done: false,
      total: run.progress?.total ?? null,
      archived: run.progress?.archived ?? [],
      skipped: run.progress?.skipped ?? [],
      remaining: 0,
    };
  }

  /** Finished tidies are kept an hour, for a caller asking again after a lost answer. */
  private forgetOld(now: number) {
    for (const [key, run] of this.runs)
      if ((run.result !== null || run.error !== null) && now - run.startedAt > 3_600_000)
        this.runs.delete(key);
  }
}
